import { jsxs, jsx } from 'react/jsx-runtime';
import { useSidebar, Sidebar, SidebarHeader, SidebarContent, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarFooter } from '../ui/sidebar.js';
import { useSidebarContext } from '../../contexts/SidebarContext.js';
import { Settings } from 'lucide-react';
import { useState, useCallback, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';

function StandardSidebar({ checkUserSettings, checkOrgSettings, organizationSelected = false, variant, logoSrc = '/assets/images/logos/logo-color.png', }) {
    const location = useLocation();
    const { state, setSettings } = useSidebarContext();
    const { isMobile, setOpenMobile } = useSidebar();
    const [actualTheme, setActualTheme] = useState('light');
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    // Function to handle mobile sidebar collapse on menu item click
    const handleMobileMenuClick = () => {
        if (isMobile) {
            setOpenMobile(false);
        }
    };
    // Check if a sidebar item should show as active based on URL prefix matching
    const isItemActive = useCallback((item) => {
        const pathname = location.pathname;
        if (item.url === '/')
            return pathname === '/';
        if (pathname.startsWith(item.url))
            return true;
        return item.activeMatch?.some(match => pathname.startsWith(match)) ?? false;
    }, [location.pathname]);
    // Default settings items
    const defaultUserSettings = [
        {
            title: 'Settings',
            url: '/user/settings',
            icon: Settings,
        },
    ];
    const defaultOrganizationSettings = [
        {
            title: 'Settings',
            url: '/organization/settings',
            icon: Settings,
        },
    ];
    // Update actual theme based on current theme setting
    useEffect(() => {
        const updateActualTheme = () => {
            const isDark = document.documentElement.classList.contains('dark') ||
                window.matchMedia('(prefers-color-scheme: dark)').matches;
            setActualTheme(isDark ? 'dark' : 'light');
        };
        updateActualTheme();
        // Listen for theme changes
        const observer = new MutationObserver(updateActualTheme);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', updateActualTheme);
        return () => {
            observer.disconnect();
            mediaQuery.removeEventListener('change', updateActualTheme);
        };
    }, []);
    // Load settings configuration
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const [userResult, orgResult] = await Promise.all([
                    checkUserSettings
                        ? Promise.resolve(checkUserSettings()).then(result => result)
                        : Promise.resolve({ success: true, data: { enabled: false } }),
                    checkOrgSettings
                        ? Promise.resolve(checkOrgSettings()).then(result => result)
                        : Promise.resolve({ success: true, data: { enabled: false } }),
                ]);
                const userSettingsEnabled = userResult?.success && userResult.data?.enabled === true; // Default to disabled
                const orgSettingsEnabled = orgResult?.success && orgResult.data?.enabled === true; // Default to disabled
                setSettings({
                    userSettings: {
                        enabled: userSettingsEnabled,
                    },
                    organizationSettings: {
                        enabled: orgSettingsEnabled && organizationSelected,
                    },
                });
                setSettingsLoaded(true);
            }
            catch (error) {
                console.warn('Failed to load settings configuration:', error);
                // Set defaults on error
                setSettings({
                    userSettings: { enabled: false },
                    organizationSettings: { enabled: false },
                });
                setSettingsLoaded(true);
            }
        };
        if (!settingsLoaded) {
            loadSettings();
        }
    }, [checkUserSettings, checkOrgSettings, organizationSelected, setSettings, settingsLoaded]);
    return (jsxs(Sidebar, { variant: variant, children: [jsxs(SidebarHeader, { className: "p-2", children: [jsx("div", { className: "w-full", children: jsx("div", { className: "flex items-center justify-start p-2 rounded-md hover:bg-accent/50 transition-colors", children: jsx("img", { src: logoSrc, alt: "Logo", className: "h-12 w-auto" }) }) }), state.header] }), jsxs(SidebarContent, { children: [state.baseItems.filter(item => !item.hidden).length > 0 && (jsxs(SidebarGroup, { children: [jsx(SidebarGroupLabel, { children: "Navigation" }), jsx(SidebarGroupContent, { children: jsx(SidebarMenu, { children: state.baseItems
                                        .filter(item => !item.hidden)
                                        .map(item => (jsx(SidebarMenuItem, { children: jsx(SidebarMenuButton, { asChild: !item.disabled, disabled: item.disabled, isActive: isItemActive(item), className: item.disabled ? 'opacity-50 cursor-not-allowed' : '', children: item.disabled ? (jsxs("div", { className: "flex items-center gap-2", children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) : (jsxs(Link, { to: item.url, onClick: handleMobileMenuClick, children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) }) }, item.title))) }) })] })), state.groups
                        .filter(group => !group.hidden)
                        .map(group => {
                        const visibleItems = group.items.filter(item => !item.hidden);
                        if (visibleItems.length === 0)
                            return null;
                        return (jsxs(SidebarGroup, { children: [jsx(SidebarGroupLabel, { className: group.disabled ? 'opacity-50' : '', children: group.label }), jsx(SidebarGroupContent, { children: jsx(SidebarMenu, { children: visibleItems.map(item => (jsx(SidebarMenuItem, { children: jsx(SidebarMenuButton, { asChild: !item.disabled && !group.disabled, disabled: item.disabled || group.disabled, isActive: isItemActive(item), className: item.disabled || group.disabled ? 'opacity-50 cursor-not-allowed' : '', children: item.disabled || group.disabled ? (jsxs("div", { className: "flex items-center gap-2", children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) : (jsxs(Link, { to: item.url, onClick: handleMobileMenuClick, children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) }) }, item.title))) }) })] }, group.label));
                    }), state.settings?.userSettings?.enabled === true && (jsxs(SidebarGroup, { children: [jsx(SidebarGroupLabel, { children: "User" }), jsx(SidebarGroupContent, { children: jsx(SidebarMenu, { children: (state.settings?.userSettings?.items || defaultUserSettings)
                                        .filter(item => !item.hidden)
                                        .map(item => (jsx(SidebarMenuItem, { children: jsx(SidebarMenuButton, { asChild: !item.disabled, disabled: item.disabled, isActive: isItemActive(item), className: item.disabled ? 'opacity-50 cursor-not-allowed' : '', children: item.disabled ? (jsxs("div", { className: "flex items-center gap-2", children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) : (jsxs(Link, { to: item.url, onClick: handleMobileMenuClick, children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) }) }, item.title))) }) })] })), state.settings?.organizationSettings?.enabled === true && (jsxs(SidebarGroup, { children: [jsx(SidebarGroupLabel, { children: "Organization" }), jsx(SidebarGroupContent, { children: jsx(SidebarMenu, { children: (state.settings?.organizationSettings?.items || defaultOrganizationSettings)
                                        .filter(item => !item.hidden)
                                        .map(item => (jsx(SidebarMenuItem, { children: jsx(SidebarMenuButton, { asChild: !item.disabled, disabled: item.disabled, isActive: isItemActive(item), className: item.disabled ? 'opacity-50 cursor-not-allowed' : '', children: item.disabled ? (jsxs("div", { className: "flex items-center gap-2", children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) : (jsxs(Link, { to: item.url, onClick: handleMobileMenuClick, children: [jsx(item.icon, {}), jsx("span", { children: item.title })] })) }) }, item.title))) }) })] }))] }), jsx(SidebarFooter, { children: state.footer })] }));
}

export { StandardSidebar };
//# sourceMappingURL=StandardSidebar.js.map
